import { forwardRef, useState, useMemo, useCallback } from 'react'
import type { JSX } from 'react'
import type { ComponentPropsWithoutRef, RefObject } from 'react'

import {
  Flex,
  Popover,
  IconButton,
  Skeleton,
  VisuallyHidden,
  ClipboardListIcon,
  CoachMark,
  Button,
} from '@buffer-mono/popcorn'

import { useCurrentOrganization } from '~publish/contexts/AccountContext'
import type { FragmentOf } from '~publish/graphql'

import { useSavedRepliesKeyboardNav } from './hooks/useSavedRepliesKeyboardNav'
import { SavedReplyEmptyState } from './SavedReplyEmptyState'
import { SavedReplyErrorState } from './SavedReplyErrorState'
import { SavedReplyHeader } from './SavedReplyHeader'
import { SavedReplyItem } from './SavedReplyItem'
import type { SavedReplyItem_SavedReply } from './SavedReplyItem'
import { SavedRepliesPaywall } from '../../components/SavedRepliesPaywall/SavedRepliesPaywall'
import { useSavedRepliesQuery } from '../../hooks/savedReplies/useSavedRepliesQuery'
import { useSavedRepliesCoachmark } from '../../hooks/ui/useSavedRepliesCoachmark'
import { SavedReplyForm } from '../SavedReplyForm/SavedReplyForm'

import styles from './SavedRepliesPopover.module.css'

export const SavedRepliesTriggerButton = forwardRef<
  HTMLButtonElement,
  Omit<ComponentPropsWithoutRef<typeof IconButton>, 'label' | 'children'>
>((props, ref) => (
  <IconButton
    ref={ref}
    label="Open saved replies"
    variant="tertiary"
    tooltip="Saved replies"
    {...props}
  >
    <ClipboardListIcon />
  </IconButton>
))

SavedRepliesTriggerButton.displayName = 'SavedRepliesTriggerButton'

const SavedRepliesTrigger = (): JSX.Element => (
  <Popover.Trigger>
    <SavedRepliesTriggerButton />
  </Popover.Trigger>
)

const coachMarkContentStyle: React.CSSProperties &
  Record<`--${string}`, string> = {
  '--coach-mark-max-width': '350px',
}

const spotlightStyle: React.CSSProperties & Record<`--${string}`, string> = {
  '--spotlight-top': '50%',
  '--spotlight-left': '50%',
}

const SavedRepliesCoachmark = ({
  onDismiss,
  onOpenSavedReplies,
}: {
  onDismiss: () => void
  onOpenSavedReplies: () => void
}): JSX.Element => (
  <CoachMark defaultOpen={true} onDismiss={onDismiss} dismissTimeout={null}>
    <CoachMark.Overlay>
      <CoachMark.Spotlight size="medium" style={spotlightStyle} />
      <SavedRepliesTrigger />
    </CoachMark.Overlay>
    <CoachMark.Content style={coachMarkContentStyle} showCloseIcon={false}>
      <CoachMark.Title>Save the replies that you use often</CoachMark.Title>
      <CoachMark.Description>
        Quickly respond to common questions or recurring topics with pre-written
        replies.
      </CoachMark.Description>
      <CoachMark.Footer>
        <Button variant="tertiary" onClick={onDismiss}>
          Maybe Later
        </Button>
        <Button variant="primary" onClick={onOpenSavedReplies}>
          Check It Out
        </Button>
      </CoachMark.Footer>
    </CoachMark.Content>
  </CoachMark>
)

const noop = (): void => undefined

export const SavedRepliesEmptyPopover = ({
  onOpenChange,
}: {
  onOpenChange?: (open: boolean) => void
}): JSX.Element => (
  <Popover onOpenChange={onOpenChange}>
    <SavedRepliesTrigger />
    <Popover.Content side="top" size="medium" className={styles.popoverContent}>
      <SavedReplyHeader
        setOpenForm={noop}
        hasReachedLimit={false}
        hasSavedReplies={false}
        setOpenPaywall={noop}
      />
      <SavedReplyEmptyState setOpenForm={noop} />
    </Popover.Content>
  </Popover>
)

export const SavedRepliesPopover = ({
  savedRepliesPopoverRef,
  handleInsertSavedReply,
}: {
  savedRepliesPopoverRef: RefObject<HTMLDivElement>
  handleInsertSavedReply: (
    savedReply: FragmentOf<typeof SavedReplyItem_SavedReply>,
  ) => void
}): JSX.Element => {
  const { savedReplies = [], loading, error } = useSavedRepliesQuery()
  const limits = useCurrentOrganization()?.limits
  const [openForm, setOpenForm] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [openPaywall, setOpenPaywall] = useState(false)
  const [activeIndex, setActiveIndex] = useState<number | null>(0)

  const hasSavedReplies = !!savedReplies?.length
  const hasReachedLimit =
    (savedReplies?.length ?? 0) > (limits?.savedReplies ?? 0)

  const handleOpenForm = useCallback(
    (open: boolean): void => {
      setActiveIndex(null)
      setOpenForm(open)
    },
    [setActiveIndex, setOpenForm],
  )

  const handleMouseEnter = useCallback((index: number): void => {
    setActiveIndex(index)
  }, [])

  const handleMouseLeave = useCallback((): void => {
    setActiveIndex(null)
  }, [])

  const skeletonLoadingView = useMemo(
    () => (
      <Flex
        direction="column"
        gap="xs"
        // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- This Flex container is the live loading status while retaining the skeleton layout.
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <VisuallyHidden>Loading saved replies...</VisuallyHidden>
        {[0, 1, 2].map((index) => (
          <Skeleton key={index} width="100%" height="72px" />
        ))}
      </Flex>
    ),
    [],
  )

  const memoizedSavedRepliesList = useMemo(() => {
    return savedReplies?.map(
      (savedReply, index: number): JSX.Element => (
        <SavedReplyItem
          key={savedReply.id}
          savedReply={savedReply}
          handleInsertSavedReply={handleInsertSavedReply}
          isSelected={activeIndex === index}
          onMouseEnter={(): void => handleMouseEnter(index)}
          onMouseLeave={handleMouseLeave}
        />
      ),
    )
  }, [
    savedReplies,
    handleInsertSavedReply,
    activeIndex,
    handleMouseLeave,
    handleMouseEnter,
  ])

  useSavedRepliesKeyboardNav({
    savedReplies,
    activeIndex,
    setActiveIndex,
    isOpen,
  })

  const { shouldShowCoachmark, onDismissCoachmark } = useSavedRepliesCoachmark()

  const handleOpenSavedReplies = useCallback(() => {
    onDismissCoachmark()
    setIsOpen(true)
  }, [onDismissCoachmark])

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      {shouldShowCoachmark ? (
        <SavedRepliesCoachmark
          onDismiss={onDismissCoachmark}
          onOpenSavedReplies={handleOpenSavedReplies}
        />
      ) : (
        <SavedRepliesTrigger />
      )}
      <Popover.Content
        side="top"
        size="medium"
        className={styles.popoverContent}
        ref={savedRepliesPopoverRef}
      >
        {/* Header */}

        <SavedReplyHeader
          setOpenForm={setOpenForm}
          hasReachedLimit={hasReachedLimit}
          hasSavedReplies={hasSavedReplies}
          setOpenPaywall={setOpenPaywall}
        />

        {/* New Reply Form */}
        <SavedReplyForm edit={false} open={openForm} setOpen={handleOpenForm} />

        {/* Loading State */}
        {loading && skeletonLoadingView}

        {/* Error State */}
        {error && <SavedReplyErrorState />}

        {/* Saved replies List */}
        {!loading && !error && hasSavedReplies && (
          <Flex
            direction="column"
            gap="xs"
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- This keyboard-navigable saved-replies collection is a listbox composite widget.
            role="listbox"
            aria-label="Saved replies list"
            tabIndex={0}
          >
            {memoizedSavedRepliesList}
          </Flex>
        )}

        {/* Empty state */}
        {!loading && !error && !openForm && !hasSavedReplies && (
          <SavedReplyEmptyState setOpenForm={handleOpenForm} />
        )}

        <SavedRepliesPaywall
          open={openPaywall}
          setOpen={setOpenPaywall}
          upgradePathName="community-saved-replies-paywall"
          ctaProperties={{
            view: 'community',
            location: 'saved-replies-popover',
            button: 'upgrade',
          }}
        />
      </Popover.Content>
    </Popover>
  )
}
